import clsx from 'clsx';
import isEmpty from 'lodash/isEmpty';
import useTranslation from 'next-translate/useTranslation';
import { useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

// Hooks
import useErrorTranslation from '@/utils/hooks/language/useErrorTranslation';
import useLanguage from '@/utils/hooks/language/useLanguage';
import useUser from '@/utils/hooks/swr/useUser';
import useUserPendo from '@/utils/hooks/swr/useUserPendo';
import useIntersectionObserver from '@/utils/hooks/utils/useIntersectionObserver';
import useActiveTreeStructure from '@/utils/hooks/tree-structure/useActiveTreeStructure';

// Utils
import { post, put } from '@/utils/client';
import { getResizedCompanyLogoDimensions } from '@/utils/settings/platform/getResizedCompanyLogoDimensions';

// Libs
import { companyLogoImageConfig } from '@/lib/files/images';
import { getPlatformSettingsOptions } from '@/lib/settings/optionsConfiguration/platformSettings';
import { PENDO_CLASS_SETTINGS_PLATFORM_SAVE } from '@/lib/tools/pendo/class';

//type
import type { PlatformFormType } from '@/components/pages/settings/platform/types';
import type { ErrorCustom } from '@/utils/client';

// Endpoints
import { UPLOAD_MEDIA, USER_PROFILE_INFOS } from '@/routes/apiEndpoints';

// Components
import Button from '@/components/base/Button';
import H3 from '@/components/base/Headings/H3';
import CompoundBanner from '@/components/base/InfoBanner/CompoundBanner';
import StickySave from '@/components/composite/Form/StickySave';
import { PlatformAutoTranslate } from '@/components/pages/settings/platform/components/PlatformAutoTranslate';
import { PlatformBeginningQ1 } from '@/components/pages/settings/platform/components/PlatformBeginningQ1';
import { PlatformCompanyLogo } from '@/components/pages/settings/platform/components/PlatformCompanyLogo';
import { PlatformCsvDelimiter } from '@/components/pages/settings/platform/components/PlatformCsvDelimiter';
import { PlatformDateFormat } from '@/components/pages/settings/platform/components/PlatformDateFormat';
import { PlatformEnableNotifications } from '@/components/pages/settings/platform/components/PlatformEnableNotifications';
import { PlatformFirstDayWeek } from '@/components/pages/settings/platform/components/PlatformFirstDayWeek';
import { PlatformHomepage } from '@/components/pages/settings/platform/components/PlatformHomepage';
import { PlatformLanguage } from '@/components/pages/settings/platform/components/PlatformLanguage';
import { PlatformMonthlyDigest } from '@/components/pages/settings/platform/components/PlatformMonthlyDigest';
import { PlatformNumbersFormat } from '@/components/pages/settings/platform/components/PlatformNumbersFormat';
import { PlatformTimeFormat } from '@/components/pages/settings/platform/components/PlatformTimeFormat';
import { PlatformTimezone } from '@/components/pages/settings/platform/components/PlatformTimezone';
import { PlatformValidateAutoPublishing } from '@/components/pages/settings/platform/components/PlatformValidateAutoPublishing';
import { PlatformWeeklyDigest } from '@/components/pages/settings/platform/components/PlatformWeeklyDigest';

// Classname for rows with two columns
const class_row_tow_columns = 'flex flex-col space-y-m lg:flex-row lg:space-x-5 lg:space-y-0';

export function PlatformForm() {
    const { isTreeStructureActivated } = useActiveTreeStructure();
    const { user, mutateUser } = useUser();
    const { mutateUserPendo } = useUserPendo({ revalidateIfStale: false });
    const { t } = useTranslation('settings');
    const { t_error } = useErrorTranslation();
    const { setLanguageLocalStorage } = useLanguage();

    // For sticky save
    const refSaveButton = useRef<HTMLButtonElement>();
    const refBannerInfo = useRef<HTMLDivElement>();
    const saveInViewPort = useIntersectionObserver(refSaveButton, { threshold: 0 });
    const BannerInfoinViewPort = useIntersectionObserver(refBannerInfo, { threshold: 0 });

    const [isInputFileActive, setIsInputFileActive] = useState(false);
    const [companyLogoUpdated, setCompanyLogoUpdated] = useState(false);
    const [companyLogo, setCompanyLogo] = useState(user?.settings?.company_logo ?? null);
    const [formMessages, setFormMessages] = useState<{
        successMessage: boolean;
        errorMessage?: string;
    }>({
        successMessage: false,
        errorMessage: null
    });

    const settingsPlatform = getPlatformSettingsOptions(t);
    const hasPreview = Boolean(isInputFileActive || companyLogo);

    // Handle file uploader change
    const handleOnFileChange = (value: File | string) => {
        setCompanyLogoUpdated(
            !user?.settings?.company_logo ? value !== user?.settings?.company_logo : true
        );
        setCompanyLogo(null);
        setIsInputFileActive(value !== null);
        clearErrors();
        setFormMessages((prevState) => ({ ...prevState, errorMessage: null }));
    };

    // default select values
    const getDefaultValues = () => {
        const defaultValues = {};
        Object.keys(settingsPlatform).forEach((field) => {
            defaultValues[field] =
                settingsPlatform[field].find(
                    (element) => element.value === user?.settings?.[field]
                ) ?? settingsPlatform[field][0];
        });
        return defaultValues;
    };

    const formMethods = useForm<PlatformFormType>({
        defaultValues: getDefaultValues()
    });
    const {
        setError,
        handleSubmit,
        getValues,
        reset,
        clearErrors,
        formState: { isSubmitting, dirtyFields }
    } = formMethods;

    function handleRequestError(error: ErrorCustom) {
        const errorsFields = error.response?.data?.errors as Record<
            keyof ReturnType<typeof getPlatformSettingsOptions>,
            string
        >;

        if (error.status === 422 && errorsFields) {
            // Parse error object, get first error and display errors under each field
            setFormMessages((prevState) => ({
                ...prevState,
                errorMessage: t('errors:error_occured_submit_data')
            }));
            return Object.keys(errorsFields).forEach((field) => {
                const error_type = errorsFields[field][0];

                setError(field, {
                    type: 'manual',
                    message: t_error('errors:error_' + field + '_' + error_type)
                });
            });
        }

        return setFormMessages((prevState) => ({
            ...prevState,
            errorMessage: t('errors:error_occured_submit_data')
        }));
    }

    // Update settings in DB
    const updateUserSettings = async (
        data: ReturnType<typeof getPlatformSettingsOptions>,
        urlImage: string = null
    ) => {
        const postData = {
            company_logo: null
        };
        Object.entries(data)?.forEach((el) => {
            postData[el[0]] = el[1]['value'];
        });

        if (urlImage) {
            postData['company_logo'] = urlImage;
        }

        try {
            await put(USER_PROFILE_INFOS, { settings: postData });
            await Promise.all([mutateUser(), mutateUserPendo()]);
            setLanguageLocalStorage(postData['platform_language']);
            setFormMessages((prevState) => ({
                ...prevState,
                errorMessage: null,
                successMessage: true
            }));
            // Reset default values to reset dirty fields
            setCompanyLogoUpdated(false);
            reset(getValues());
        } catch (e) {
            handleRequestError(e);
        }
    };

    // Form submit
    const onSubmit = async (
        data: ReturnType<typeof getPlatformSettingsOptions> & {
            fileInput?: File;
        }
    ) => {
        setFormMessages((prevState) => ({
            ...prevState,
            errorMessage: null,
            successMessage: false
        }));

        const file = data.fileInput?.[0];

        if (file) {
            try {
                const resizedDimensions = await getResizedCompanyLogoDimensions(file, () =>
                    setFormMessages((prevState) => ({
                        ...prevState,
                        errorMessage: t('platform_page.error_occured_process_company_logo')
                    }))
                );
                const formData = new FormData();
                formData.append('file', file);
                formData.append('folder', companyLogoImageConfig.repository);
                formData.append('resize[width]', resizedDimensions.width.toString());
                formData.append('resize[height]', resizedDimensions.height.toString());
                const res = await post(UPLOAD_MEDIA, formData);
                await updateUserSettings(data, res.data.image_url);
            } catch (e) {
                handleRequestError(e);
            }
        } else {
            await updateUserSettings(data);
        }

        // Scroll To info banner if it's not visible in the viewport
        if (!BannerInfoinViewPort) {
            if (refBannerInfo?.current) {
                refBannerInfo.current?.scrollIntoView({ behavior: 'smooth' });
            }
        }
    };

    return (
        <FormProvider {...formMethods}>
            <form
                onSubmit={handleSubmit(onSubmit)}
                className={clsx({ 'p-8 xl:px-20 xl:py-10': !isTreeStructureActivated })}
                id="settings-platform-form">
                <div ref={refBannerInfo} />
                {formMessages.successMessage ? (
                    <CompoundBanner type="success" className="mb-8 w-full">
                        <CompoundBanner.Icon icon="check" />
                        <CompoundBanner.Content
                            title={t('commons:success')}
                            message={t('settings_banner_success')}
                        />
                        <CompoundBanner.Close
                            onClose={() =>
                                setFormMessages((prevState) => ({
                                    ...prevState,
                                    successMessage: false
                                }))
                            }
                        />
                    </CompoundBanner>
                ) : null}
                {formMessages.errorMessage ? (
                    <CompoundBanner className="mb-8 w-full">
                        <CompoundBanner.Icon />
                        <CompoundBanner.Content
                            title={t('commons:error')}
                            message={formMessages.errorMessage}
                        />
                        <CompoundBanner.Close
                            onClose={() =>
                                setFormMessages((prevState) => ({
                                    ...prevState,
                                    errorMessage: null
                                }))
                            }
                        />
                    </CompoundBanner>
                ) : null}
                <H3 className="mb-7 border-b border-icoGray-300 pb-3 dark:border-icoDarkMode-beton">
                    {t('platform_page.settings')}
                </H3>
                <div className="flex flex-col space-y-m">
                    <div className={class_row_tow_columns}>
                        <PlatformLanguage />
                        <PlatformAutoTranslate />
                    </div>
                    <div className={class_row_tow_columns}>
                        <PlatformHomepage />
                        <PlatformValidateAutoPublishing />
                    </div>
                    <div className={class_row_tow_columns}>
                        <PlatformEnableNotifications />
                        <PlatformWeeklyDigest />
                    </div>
                    <PlatformMonthlyDigest />
                    <div className={class_row_tow_columns}>
                        <PlatformCompanyLogo
                            companyLogo={companyLogo}
                            handleOnFileChange={handleOnFileChange}
                            hasPreview={hasPreview}
                        />
                        <PlatformCsvDelimiter />
                    </div>
                </div>
                <H3 className="mb-7 mt-12 border-b border-icoGray-300 pb-3 dark:border-icoDarkMode-beton">
                    {t('platform_page.timezone_and_data_formats')}
                </H3>
                <div className="flex flex-col space-y-m">
                    <div className={class_row_tow_columns}>
                        <PlatformTimezone />
                        <PlatformNumbersFormat />
                    </div>
                    <div className={class_row_tow_columns}>
                        <PlatformTimeFormat />
                        <PlatformDateFormat />
                    </div>
                    <div className={class_row_tow_columns}>
                        <PlatformFirstDayWeek />
                        <PlatformBeginningQ1 />
                    </div>
                </div>
                <div className="mt-8 flex justify-end">
                    <Button
                        disabled={isEmpty(dirtyFields) && !companyLogoUpdated}
                        ref={refSaveButton}
                        type="submit"
                        data-testid="submitButton"
                        isLoading={isSubmitting}
                        className={PENDO_CLASS_SETTINGS_PLATFORM_SAVE}>
                        {t('save')}
                    </Button>
                </div>
            </form>
            <div className={clsx({ '-mx-8': isTreeStructureActivated })}>
                <StickySave
                    isVisible={!saveInViewPort && (!isEmpty(dirtyFields) || companyLogoUpdated)}
                    className={clsx({ 'xl:!px-8': isTreeStructureActivated })}>
                    <Button
                        isLoading={isSubmitting}
                        size="sm"
                        className={clsx('rounded-m', PENDO_CLASS_SETTINGS_PLATFORM_SAVE)}
                        onClick={handleSubmit(onSubmit)}>
                        <p className="text-xs">{t('save')}</p>
                    </Button>
                </StickySave>
            </div>
        </FormProvider>
    );
}
